import { useApolloClient, useMutation } from '@apollo/client';
import { type BoardFragmentFragment } from '@apps/www/src/__generated__/graphql';
import useAuthBoards from '@apps/www/src/www/hooks/useAuthBoards';
import useAuthUserID from '@apps/www/src/www/hooks/useAuthUserID';
import ReorderBoardsMutation from '@apps/www/src/www/queries/ReorderBoardsMutation';
import type { DragEndEvent } from '@dnd-kit/core';
import { KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';

type Board = BoardFragmentFragment;

interface UseBoardReorderOptions {
	onError?: () => void;
}

export const useBoardReorder = (boards: Board[], options: UseBoardReorderOptions = {}) => {
	const [reorderBoards] = useMutation(ReorderBoardsMutation);
	const client = useApolloClient();
	const authBoards = useAuthBoards() ?? [];
	const authUserID = useAuthUserID();

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: { x: 8, y: 10 },
				delay: 100,
				tolerance: 5,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const handleDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event;

		if (active.id !== over?.id) {
			const oldIndex = boards.findIndex((board) => board._id === active.id);
			const newIndex = boards.findIndex((board) => board._id === over?.id);

			if (oldIndex === -1 || newIndex === -1) {
				return;
			}

			const reorderedBoards = [...boards];
			const [movedBoard] = reorderedBoards.splice(oldIndex, 1);
			reorderedBoards.splice(newIndex, 0, movedBoard);
			reorderedBoards.push(
				...authBoards.filter((b) => !reorderedBoards.some((rb) => rb._id === b._id)),
			);

			const userQuery = client.cache.identify({
				__typename: 'User',
				_id: authUserID,
			});

			if (userQuery) {
				client.cache.modify({
					id: userQuery,
					fields: {
						boards: () => {
							return [...reorderedBoards];
						},
					},
				});
				client.cache.gc();
			}

			try {
				await reorderBoards({
					variables: {
						input: {
							fromBoardID: boards[oldIndex]._id,
							toBoardID: boards[newIndex]._id,
						},
					},
				});
			} catch (error) {
				console.error(error);
				options.onError?.();

				// Revert cache to original order if mutation fails
				if (userQuery) {
					client.cache.modify({
						id: userQuery,
						fields: {
							boards: () => boards,
						},
					});
					client.cache.gc();
				}
			}
		}
	};

	return {
		sensors,
		handleDragEnd,
	};
};
